import { config } from "../../config/config";
import {
  EmailNotVerifiedError,
  GymSelectionRequiredError,
  onboardingStatusSchema,
  type AuthFormInput,
  type AvailableGym,
  type ForgotPasswordInput,
  type OnboardingStatus,
} from "../../utils/zod/auth";

async function readJsonSafe(
  response: Response,
): Promise<Record<string, unknown> | null> {
  const contentType = response.headers.get("content-type");
  if (!contentType?.includes("application/json")) return null;
  try {
    return (await response.json()) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function maybeThrowGymSelection(
  status: number,
  data: Record<string, unknown> | null,
): void {
  if (status !== 409 || !data || data.requiresGymSelection !== true) return;
  const availableGyms = Array.isArray(data.availableGyms)
    ? (data.availableGyms as AvailableGym[])
    : [];
  const message = typeof data.error === "string" ? data.error : undefined;
  throw new GymSelectionRequiredError(availableGyms, message);
}

function maybeThrowEmailNotVerified(
  status: number,
  data: Record<string, unknown> | null,
): void {
  // Se atrapa por `code` y no por el mensaje: el backend responde en ingles y
  // sin esto el usuario veria "Please verify your email before signing in".
  if (status !== 403 || data?.code !== "EMAIL_NOT_VERIFIED") return;
  throw new EmailNotVerifiedError();
}

async function handleAuthError(
  response: Response,
  fallback: string,
  statusOverrides: Record<number, string> = {},
): Promise<never> {
  const data = await readJsonSafe(response);
  maybeThrowGymSelection(response.status, data);
  maybeThrowEmailNotVerified(response.status, data);
  const message =
    (data && typeof data.error === "string" && data.error) ||
    statusOverrides[response.status] ||
    response.statusText ||
    fallback;
  throw new Error(message);
}

export const userLoginRequest = async ({
  email,
  password,
  rememberMe = false,
  gymSlug,
}: AuthFormInput) => {
  const body: Record<string, unknown> = { email, password, rememberMe };
  if (gymSlug) body.gymSlug = gymSlug;

  const response = await fetch(`${config.API_URL}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    await handleAuthError(response, "Error al iniciar sesión", {
      401: "Credenciales incorrectas",
      404: "Usuario no registrado",
    });
  }

  return response.json();
};

export const forgotPasswordRequest = async (
  body: ForgotPasswordInput,
): Promise<{ message: string }> => {
  const payload: Record<string, unknown> = {
    email: body.email.trim().toLowerCase(),
  };
  if (body.gymSlug) payload.gymSlug = body.gymSlug;

  const response = await fetch(`${config.API_URL}/auth/forgot-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    await handleAuthError(response, "Error al solicitar recuperación");
  }
  return response.json();
};

export const resetPasswordRequest = async (params: {
  token: string;
  newPassword: string;
}): Promise<{ message: string }> => {
  const response = await fetch(`${config.API_URL}/auth/reset-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      token: params.token,
      newPassword: params.newPassword,
    }),
  });
  if (!response.ok) {
    await handleAuthError(response, "Error al restablecer contraseña");
  }
  return response.json();
};

export const changePasswordRequest = async (
  body: { currentPassword: string; newPassword: string },
  token: string,
): Promise<{ ok: true; mustAcceptTerms: boolean }> => {
  const response = await fetch(`${config.API_URL}/auth/change-password`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    await handleAuthError(response, "Error al cambiar la contraseña");
  }
  return response.json();
};

export const getOnboardingStatusRequest = async (
  token: string,
): Promise<OnboardingStatus> => {
  const response = await fetch(`${config.API_URL}/auth/onboarding-status`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  if (!response.ok) {
    await handleAuthError(response, "Error al obtener el estado de la cuenta");
  }
  const parsed = onboardingStatusSchema.safeParse(await response.json());
  if (!parsed.success) {
    throw new Error("El estado de la cuenta llegó con un formato inesperado");
  }
  return parsed.data;
};
